/**
 * Your tab is running code we replaced.
 *
 * Founder, 2026-09-03, on the deploy work: proceed with this. A deploy that
 * changes the frontend bundle leaves every open tab running the JavaScript it
 * loaded hours ago. That is a genuinely different situation from "we are
 * deploying" — it is the one case where the user must actually DO something, and
 * the only one where an action is worth offering.
 *
 * HOW IT KNOWS, without any new infrastructure. Vite hashes the entry bundle:
 * `/assets/index-Nb82Jjdj.js`. That filename changes when, and only when, the
 * bundle changes — so it already IS the build identity, and index.html already
 * publishes it. This compares the entry script the CURRENT PAGE loaded against
 * the one index.html now names.
 *
 * A build-time constant would have been the obvious alternative and is worse: it
 * says which build this tab is, and nothing about which build is current, so it
 * needs a second source of truth to compare against and a way to keep the two in
 * step. The hash needs neither.
 *
 * WHAT IT DELIBERATELY DOES NOT DO:
 *   - RELOAD BY ITSELF. "When convenient" is the whole point. A page that
 *     reloads under someone loses whatever they were typing, and it would do it
 *     at the least predictable moment — mid-sentence, mid-form, during a demo.
 *   - SHOW DURING A DEPLOY. The deploy strip is already saying something, and a
 *     new bundle mid-deploy is expected rather than news. This waits until the
 *     deploy is over, when the message is both true and actionable.
 *   - BLOCK ANYTHING. Same corner and the same rules as the deploy strip, except
 *     that this one carries a button and therefore must take pointer events —
 *     which is exactly why it is small, in a corner, and dismissible.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw, X } from "lucide-react";

/** Slow on purpose: a stale tab is not urgent, and this fetches index.html. */
const POLL_MS = 120_000;

/** The entry bundle this page is running, from its own document. */
function currentEntry(): string | null {
  const el = document.querySelector<HTMLScriptElement>(
    'script[type="module"][src*="/assets/index-"]',
  );
  return el?.getAttribute("src") ?? null;
}

/** The entry bundle index.html names right now. */
async function publishedEntry(): Promise<string | null> {
  try {
    // cache: "no-store" matters — a cached index.html would report the build
    // this tab already has, forever, and the check would never fire.
    const res = await fetch(`/index.html?v=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return null;
    const html = await res.text();
    return html.match(/\/assets\/index-[A-Za-z0-9_-]+\.js/)?.[0] ?? null;
  } catch {
    return null;
  }
}

export function NewVersionAvailable({ deploying = false }: { deploying?: boolean }) {
  const [stale, setStale] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const mine = useRef<string | null>(null);

  useEffect(() => {
    mine.current = currentEntry();
  }, []);

  const check = useCallback(async () => {
    // Nothing to compare against when the page was not served with a hashed
    // entry (a dev server, or a test). Silence beats a false alarm.
    if (!mine.current) return;
    const published = await publishedEntry();
    if (published && !mine.current.includes(published)) setStale(true);
  }, []);

  useEffect(() => {
    const id = setInterval(check, POLL_MS);
    return () => clearInterval(id);
  }, [check]);

  // Suppressed while a deploy is running: a changed bundle mid-deploy is
  // expected, and two notices at once is the obstruction we just removed.
  if (!stale || dismissed || deploying) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="new-version-available"
      className="fixed bottom-4 left-4 z-40 flex items-center gap-3 rounded-full border border-border bg-surface-elevated/95 px-3 py-1.5 shadow-sm backdrop-blur"
    >
      <span className="text-xs text-text-primary">A new version is available</span>
      <button
        type="button"
        data-testid="new-version-reload"
        onClick={() => window.location.reload()}
        className="flex items-center gap-1 rounded-full bg-brand-500 px-2.5 py-1 text-xs text-white"
      >
        <RefreshCw className="h-3 w-3" aria-hidden />
        Reload
      </button>
      <button
        type="button"
        aria-label="Dismiss"
        data-testid="new-version-dismiss"
        onClick={() => setDismissed(true)}
        className="text-text-secondary"
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  );
}
